import { css } from '@emotion/react';
import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { DatePickerInput } from '../../../components/DatePickerInput/DatePickerInput';
import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import { AuditorDateRangesPreferences } from '../../../data-models/preferences-v3.data-model';
import { ConfigStateChip } from '../components/ConfigStateChip';
import { DetailsConfigActions } from '../DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../DataDisplay/ExitConfigBlocker';
import { useAuditorDateRangesConfigFormState } from './useAuditorDateRangesConfigFormState';

/**
 * Convert date string (e.g., "2024-01-01") to Date object
 */
function stringToDate(dateStr: string): Date | null {
  if (!dateStr) {
    return null;
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date;
}

/**
 * Convert Date object to date string (e.g., Date -> "2024-01-01")
 */
function dateToString(date: Date | null): string {
  if (!date) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const sectionCss = css`
  border: 1px solid var(--mui-colors-neutral-30);
  border-radius: 8px;
  padding: 1rem;
`;

export function AuditorDateRangesConfig() {
  const { editStatus, methods, onSave, onReset } = useAuditorDateRangesConfigFormState();

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' padding='8px 12px 12px' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <Stack direction='row' alignItems={'start'} columnGap='0.5rem' mb='1rem'>
          <Typography variant='h4'>{`${getRouteTitle(ROUTES.ADMIN_PANEL_AUDITOR_CONFIG)}`}</Typography>
          <ConfigStateChip configStatus={editStatus} />
        </Stack>
        <Stack
          direction={'row'}
          gap={'1rem'}
          alignItems={'center'}
          justifyContent={'space-between'}
          mb='2rem'
        >
          <Typography variant='body2' color='text.secondary'>
            Configure date range restrictions for users with auditor access. Changes take effect immediately
            after saving.
          </Typography>
          <DetailsConfigActions
            save={{ handler: onSave, disabled: editStatus !== 'modified' }}
            reset={{ handler: onReset }}
          />
        </Stack>
        <Stack gap='1.5rem'>
          <DateRangeSection />
          <RestrictionsSection />
        </Stack>
      </Stack>
    </FormProvider>
  );
}

function DateRangeSection() {
  const {
    control,
    formState: { errors },
  } = useFormContext<AuditorDateRangesPreferences>();

  return (
    <Stack gap='1rem' css={sectionCss}>
      <Typography variant='h6'>Date Range Settings</Typography>
      <Typography variant='body2' color='text.secondary'>
        Configure the date range constraints for users with auditor access. Select the start and end dates.
        Users will only be able to view financial data within the specified date range.
      </Typography>
      <Stack direction='row' gap='1rem'>
        <Stack flex={1} gap='0.5rem'>
          <Typography variant='caption' color='text.secondary'>
            Start Date
          </Typography>
          <Controller
            name='startDate'
            control={control}
            render={({ field }) => (
              <DatePickerInput
                value={stringToDate(field.value)}
                onChange={(date) => field.onChange(dateToString(date))}
                placeholder='Select start date'
                minDate={new Date('2020-01-01')}
                maxDate={new Date()}
              />
            )}
          />
          {errors.startDate && (
            <Typography variant='caption' color='error'>
              {errors.startDate.message}
            </Typography>
          )}
        </Stack>
        <Stack flex={1} gap='0.5rem'>
          <Typography variant='caption' color='text.secondary'>
            End Date
          </Typography>
          <Controller
            name='endDate'
            control={control}
            render={({ field }) => (
              <DatePickerInput
                value={stringToDate(field.value)}
                onChange={(date) => field.onChange(dateToString(date))}
                placeholder='Select end date'
                minDate={new Date('2020-01-01')}
                maxDate={new Date()}
              />
            )}
          />
          {errors.endDate && (
            <Typography variant='caption' color='error'>
              {errors.endDate.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

function RestrictionsSection() {
  const { control } = useFormContext<AuditorDateRangesPreferences>();

  return (
    <Stack gap='1rem' css={sectionCss}>
      <Typography variant='h6'>Restriction Settings</Typography>
      <Controller
        name='restrictToRange'
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} />}
            label='Enforce date range restrictions for auditors'
          />
        )}
      />
      <Typography variant='body2' color='text.secondary'>
        When enabled, users with auditor access will only see data within the configured date range.
      </Typography>
    </Stack>
  );
}
